import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { MessageCircle, ArrowRight, Star, CheckCircle2, ChevronDown, ShieldCheck, Headset, MapPin, TrendingUp } from "lucide-react";
import { serviceList, heroImage, HERO_VIDEO_URL, testimonials } from "../data/mock";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import CTASection from "../components/CTASection";
import LeadForm from "../components/LeadForm";
import HeroDashboard from "../components/HeroDashboard";
import IndiaMapSection from "../components/IndiaMapSection";
import { useLanguage } from "../i18n/LanguageContext";
import { usePublicContent } from "../i18n/PublicContentContext";

const whyIcons = [ShieldCheck, Headset, MapPin, TrendingUp];
const resolveImg = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function Home() {
  const [openFaq, setOpenFaq] = React.useState(0);
  const { t, lang } = useLanguage();
  const pub = usePublicContent();
  const location = useLocation();

  const svcMap = React.useMemo(() => {
    const m = {};
    (pub.services_meta || []).forEach((x) => { m[x.slug] = x; });
    return m;
  }, [pub.services_meta]);
  const pickL = (o, en, hi) => (lang === "hi" ? (o?.[hi] || o?.[en] || "") : (o?.[en] || ""));
  const svcTitle = (slug) => pickL(svcMap[slug], "title_en", "title_hi") || t.services[slug].title;
  const svcShort = (slug) => pickL(svcMap[slug], "short_en", "short_hi") || t.services[slug].short;
  const activeServices = serviceList.filter((s) => svcMap[s.slug]?.active !== false);

  // Dynamic content with fallback to translation defaults
  const L = (ov, en, hi, def) => {
    const v = lang === "hi" ? (ov?.[hi] || ov?.[en]) : ov?.[en];
    return v && String(v).trim() ? v : def;
  };

  const heroOv = pub.hero || {};
  const heroHeadingDefault = `${t.hero.line1}\n${t.hero.line2}\n${t.hero.line3}`;
  const heroHeadingLines = L(heroOv, "heading_en", "heading_hi", heroHeadingDefault).split("\n");
  const heroSub = L(heroOv, "sub_en", "sub_hi", t.hero.sub);
  const heroPara = L(heroOv, "para_en", "para_hi", t.hero.paragraph);
  const heroVideo = (heroOv.bg_video && heroOv.bg_video.trim()) || HERO_VIDEO_URL;

  const whyOv = pub.why || {};
  const whyTitle = L(whyOv, "title_en", "title_hi", t.whyChoose.title);
  const whySubtitle = L(whyOv, "subtitle_en", "subtitle_hi", t.whyChoose.subtitle);
  const whyItems = (whyOv.items && whyOv.items.length > 0)
    ? whyOv.items.filter((x) => x.active !== false).map((x) => ({ h: L(x, "title_en", "title_hi", ""), p: L(x, "desc_en", "desc_hi", "") }))
    : t.whyChoose.reasons;

  // Editable section headings / eyebrows
  const sec = pub.sections || {};
  const soOv = sec.services || {}, tsOv = sec.testimonials || {}, fqOv = sec.faq || {}, ldOv = sec.lead || {};
  const so = {
    eyebrow: L(soOv, "eyebrow_en", "eyebrow_hi", t.servicesOverview.eyebrow),
    title: L(soOv, "title_en", "title_hi", t.servicesOverview.title),
    subtitle: L(soOv, "subtitle_en", "subtitle_hi", t.servicesOverview.subtitle),
    viewAll: L(soOv, "viewall_en", "viewall_hi", t.servicesOverview.viewAll),
  };
  const ts = {
    eyebrow: L(tsOv, "eyebrow_en", "eyebrow_hi", t.testimonialsSection.eyebrow),
    title: L(tsOv, "title_en", "title_hi", t.testimonialsSection.title),
    subtitle: L(tsOv, "subtitle_en", "subtitle_hi", ""),
  };
  const fq = {
    eyebrow: L(fqOv, "eyebrow_en", "eyebrow_hi", t.faqSection.eyebrow),
    title: L(fqOv, "title_en", "title_hi", t.faqSection.title),
    subtitle: L(fqOv, "subtitle_en", "subtitle_hi", ""),
  };
  const ld = {
    eyebrow: L(ldOv, "eyebrow_en", "eyebrow_hi", t.lead.eyebrow),
    title: L(ldOv, "title_en", "title_hi", t.lead.title),
    paragraph: L(ldOv, "para_en", "para_hi", t.lead.paragraph),
    whatsappPrefer: L(ldOv, "whatsapp_en", "whatsapp_hi", t.lead.whatsappPrefer),
  };
  const ldBullets = (() => {
    const v = lang === "hi" ? (ldOv.bullets_hi || ldOv.bullets_en) : ldOv.bullets_en;
    if (v && String(v).trim()) return String(v).split("\n").map((x) => x.trim()).filter(Boolean);
    return t.lead.bullets;
  })();

  const tItems = (pub.testimonials && pub.testimonials.length > 0)
    ? pub.testimonials.map((x) => ({ name: x.name, quote: lang === "hi" ? (x.quote_hi || x.quote_en) : x.quote_en, image: x.image }))
    : t.testimonials.map((tm, i) => ({ name: tm.name, quote: tm.quote, image: testimonials[i]?.image ?? null }));

  const faqItems = (pub.faqs && pub.faqs.length > 0)
    ? pub.faqs.map((x) => ({ q: lang === "hi" ? (x.q_hi || x.q_en) : x.q_en, a: lang === "hi" ? (x.a_hi || x.a_en) : x.a_en }))
    : t.faqs;

  React.useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 120);
    }
  }, [location]);

  return (
    <div className="bg-white overflow-hidden">
      <Helmet>
        <title>Superhost Cloud | India's Fastest Growing Hospitality Technology</title>
        <meta name="description" content="Superhost Cloud helps Indian hotel owners get more bookings, better online visibility and effortless property management." />
      </Helmet>

      {/* HERO */}
      <section className="relative min-h-[100svh] lg:min-h-screen flex items-center pt-24 pb-10 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0">
          {heroVideo ? (
            <video
              className="w-full h-full object-cover"
              src={heroVideo}
              poster={heroImage}
              autoPlay
              loop
              muted
              playsInline
              aria-hidden="true"
              data-testid="hero-bg-video"
            />
          ) : (
            <img src={heroImage} alt="Luxury hotel" className="w-full h-full object-cover" />
          )}
          {/* Semi-transparent dark overlay (35%) — keeps text readable while letting the video show through */}
          <div className="absolute inset-0 bg-black/35" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-7 lg:gap-8 items-center">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-[#12284B] border border-[#12284B] rounded-full px-4 py-1.5 mb-6 backdrop-blur-sm"
            >
              <span className="h-2 w-2 rounded-full bg-[#E84B1C] animate-pulse" />
              <span className="text-white text-sm font-medium">{t.hero.badge}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-head font-extrabold leading-[1.02] tracking-tight text-balance"
            >
              {heroHeadingLines.map((ln, i) => (
                <span key={i} className={`block text-2xl sm:text-3xl lg:text-4xl text-white`}>{ln}</span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-6 inline-block bg-[#12284B] !text-white rounded-2xl px-6 py-4 font-head font-bold text-xl sm:text-2xl lg:text-3xl leading-snug max-w-2xl text-balance shadow-xl shadow-[#12284B]/25"
            >
              {heroSub}
            </motion.p>

            {heroPara && (
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.45 }}
                className="mt-6 text-lg lg:text-xl text-black/70 max-w-2xl leading-relaxed"
              >
                {heroPara}
              </motion.p>
            )}

          </div>

          <div className="lg:pl-4">
            <HeroDashboard stats={pub.stats?.items} lang={lang} />
          </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl">
            <span className="font-head text-[#E84B1C] font-semibold tracking-wide uppercase text-sm">{so.eyebrow}</span>
            <h2 className="mt-3 font-head text-3xl md:text-5xl font-bold text-[#12284B] leading-tight">{so.title}</h2>
            <p className="mt-4 text-black/70 text-lg">{so.subtitle}</p>
          </Reveal>

          <Stagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeServices.map((s) => {
              const Icon = s.icon;
              const to = s.detail ? `/services/${s.slug}` : "/services";
              return (
                <StaggerItem key={s.slug}>
                  <Link
                    to={to}
                    className="group block h-full bg-[#17205C] border border-white/10 hover:border-[#E84B1C]/50 rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1.5 shadow-sm hover:shadow-2xl hover:shadow-[#12284B]/20"
                  >
                    <div className="h-14 w-14 rounded-xl bg-[#E84B1C]/20 flex items-center justify-center group-hover:bg-[#E84B1C] transition-colors duration-300">
                      <Icon size={26} className="text-[#E84B1C] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="mt-5 font-head text-xl font-semibold text-white">{svcTitle(s.slug)}</h3>
                    <p className="mt-2.5 text-white/70 text-sm leading-relaxed">{svcShort(s.slug)}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-[#E84B1C] font-head font-medium text-sm">
                      {t.servicesOverview.learnMore}
                      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                </StaggerItem>
              );
            })}
          </Stagger>

          <Reveal className="mt-12 text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-[#E84B1C] hover:bg-[#F0673E] text-white font-head font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105"
            >
              {so.viewAll} <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <span className="font-head text-[#E84B1C] font-semibold tracking-wide uppercase text-sm">{ts.eyebrow}</span>
            <h2 className="mt-3 font-head text-3xl md:text-5xl font-bold text-[#12284B] leading-tight">{ts.title}</h2>
            {ts.subtitle && <p className="mt-4 text-black/70 text-lg">{ts.subtitle}</p>}
          </Reveal>

          <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {tItems.map((tm) => {
              const image = tm.image ?? null;
              return (
              <StaggerItem key={tm.name}>
                <div className="h-full bg-[#17205C] border border-white/10 rounded-2xl p-8 shadow-sm hover:border-[#E84B1C]/40 hover:shadow-lg hover:shadow-[#12284B]/20 transition-all duration-300">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={18} className="text-[#E84B1C]" fill="#E84B1C" />
                    ))}
                  </div>
                  <p className="text-white text-base leading-relaxed">“{tm.quote}”</p>
                  <div className="mt-6 flex items-center gap-3">
                    {image ? (
                      <img
                        src={resolveImg(image)}
                        alt={tm.name}
                        className="h-12 w-12 rounded-full object-cover border-2 border-[#E84B1C]/50 shrink-0"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-[#E84B1C]/20 flex items-center justify-center font-head font-bold text-[#E84B1C] shrink-0">
                        {tm.name.charAt(0)}
                      </div>
                    )}
                    <div className="font-head font-semibold text-white">{tm.name}</div>
                  </div>
                </div>
              </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* INDIA PRESENCE MAP */}
      {pub.map?.enabled !== false && <IndiaMapSection data={pub.map} lang={lang} />}

      {/* WHY CHOOSE US */}
      <section id="why-choose-us" data-testid="why-choose-us-section" className="py-20 lg:py-28 bg-[#F4F6FA] border-y border-[#12284B]/10 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl">
            <span className="font-head text-[#E84B1C] font-semibold tracking-wide uppercase text-sm">{t.whyChoose.eyebrow}</span>
            <h2 className="mt-3 font-head text-3xl md:text-5xl font-bold text-[#12284B] leading-tight">{whyTitle}</h2>
            <p className="mt-4 text-black/70 text-lg leading-relaxed">{whySubtitle}</p>
          </Reveal>

          <Stagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {whyItems.map((r, i) => {
              const Icon = whyIcons[i % whyIcons.length];
              return (
                <StaggerItem key={r.h}>
                  <div className="h-full bg-white border border-[#12284B]/10 rounded-2xl p-7 flex gap-5 shadow-sm hover:shadow-xl hover:shadow-[#12284B]/10 hover:-translate-y-1 transition-all duration-300">
                    <div className="h-13 w-13 shrink-0 rounded-xl bg-[#E84B1C]/12 flex items-center justify-center p-3">
                      <Icon size={26} className="text-[#E84B1C]" />
                    </div>
                    <div>
                      <h3 className="font-head text-xl font-semibold text-[#12284B]">{r.h}</h3>
                      <p className="mt-2 text-black/65 text-sm leading-relaxed">{r.p}</p>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* LEAD FORM */}
      <section id="lead" className="py-20 lg:py-28 bg-[#F4F6FA] border-y border-[#12284B]/10">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="font-head text-[#E84B1C] font-semibold tracking-wide uppercase text-sm">{ld.eyebrow}</span>
            <h2 className="mt-3 font-head text-3xl md:text-5xl font-bold text-[#12284B] leading-tight">{ld.title}</h2>
            <p className="mt-4 text-black/70 text-lg leading-relaxed">{ld.paragraph}</p>
            <ul className="mt-7 space-y-4">
              {ldBullets.map((b) => (
                <li key={b} className="flex items-center gap-3 text-[#12284B]">
                  <CheckCircle2 size={20} className="text-[#E84B1C] shrink-0" /> {b}
                </li>
              ))}
            </ul>
            <a
              href={pub.waLink()}
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex items-center gap-2 text-[#12284B] font-head font-semibold border border-[#12284B]/25 hover:border-[#E84B1C] px-6 py-3 rounded-full transition-all duration-300 hover:bg-[#12284B]/5"
            >
              <MessageCircle size={18} className="text-[#25D366]" /> {ld.whatsappPrefer}
            </a>
          </Reveal>

          <Reveal delay={0.15}>
            <LeadForm />
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center">
            <span className="font-head text-[#E84B1C] font-semibold tracking-wide uppercase text-sm">{fq.eyebrow}</span>
            <h2 className="mt-3 font-head text-3xl md:text-5xl font-bold text-[#12284B] leading-tight">{fq.title}</h2>
            {fq.subtitle && <p className="mt-4 text-black/70 text-lg">{fq.subtitle}</p>}
          </Reveal>

          <div className="mt-12 space-y-4">
            {faqItems.map((fq, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="bg-white border border-[#12284B]/10 rounded-xl overflow-hidden shadow-sm">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-head font-semibold text-[#12284B] text-lg">{fq.q}</span>
                    <ChevronDown size={22} className={`text-[#E84B1C] shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`grid transition-all duration-300 ${openFaq === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <p className="px-6 pb-5 text-black/65 leading-relaxed">{fq.a}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
